/**
 * Hero Section Block for Editor.js
 * Creates email-safe hero sections with headline, subtitle, and CTA
 */

import { BlockTool } from '@editorjs/editorjs';

export interface HeroBlockData {
  headline: string;
  subtitle: string;
  buttonText: string;
  buttonUrl: string;
  backgroundColor: 'wine' | 'gold' | 'teal' | 'dark';
}

export default class HeroBlock implements BlockTool {
  private data: HeroBlockData;
  private wrapper: HTMLElement | null = null;

  static get toolbox() {
    return {
      title: 'Hero Section',
      icon: '<svg width="17" height="15" viewBox="0 0 17 15" xmlns="http://www.w3.org/2000/svg"><rect x="1" y="1" width="15" height="13" rx="1" stroke="currentColor" stroke-width="1.5" fill="none"/><path d="M4 5h9M4 8h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    };
  }

  constructor({ data }: { data: HeroBlockData }) {
    this.data = {
      headline: data.headline || 'Your Headline Here',
      subtitle: data.subtitle || 'Your subtitle or description goes here',
      buttonText: data.buttonText || 'Shop Now',
      buttonUrl: data.buttonUrl || 'https://www.ejswineshop.com',
      backgroundColor: data.backgroundColor || 'wine',
    };
  }

  render(): HTMLElement {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('email-hero-block');
    this.updateWrapper();
    return this.wrapper;
  }

  private getBackgroundColor(): string {
    const colors: Record<string, string> = {
      wine: '#722f37',
      gold: '#D4A657',
      teal: '#7B9D9D',
      dark: '#2b2b2b',
    };
    return colors[this.data.backgroundColor] || colors.wine;
  }

  private getButtonColor(): string {
    // Contrasting button colors
    const buttonColors: Record<string, string> = {
      wine: '#D4A657',
      gold: '#2b2b2b',
      teal: '#D4A657',
      dark: '#D4A657',
    };
    return buttonColors[this.data.backgroundColor] || buttonColors.wine;
  }

  private updateWrapper(): void {
    if (!this.wrapper) return;

    const bgColor = this.getBackgroundColor();
    const btnColor = this.getButtonColor();

    this.wrapper.style.cssText = `
      background-color: ${bgColor};
      padding: 48px 32px;
      text-align: center;
      border-radius: 4px;
      margin: 8px 0;
    `;

    this.wrapper.innerHTML = '';

    // Headline
    const headline = document.createElement('h2');
    headline.contentEditable = 'true';
    headline.textContent = this.data.headline;
    headline.style.cssText = `
      font-size: 32px;
      font-weight: 700;
      line-height: 40px;
      color: #ffffff;
      margin: 0 0 12px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      outline: none;
    `;
    headline.addEventListener('input', () => {
      this.data.headline = headline.textContent || '';
    });

    // Subtitle
    const subtitle = document.createElement('p');
    subtitle.contentEditable = 'true';
    subtitle.textContent = this.data.subtitle;
    subtitle.style.cssText = `
      font-size: 18px;
      line-height: 28px;
      color: #ffffff;
      margin: 0 0 24px;
      opacity: 0.9;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      outline: none;
    `;
    subtitle.addEventListener('input', () => {
      this.data.subtitle = subtitle.textContent || '';
    });

    // Button
    const button = document.createElement('a');
    button.href = this.data.buttonUrl;
    button.contentEditable = 'true';
    button.textContent = this.data.buttonText;
    button.style.cssText = `
      background-color: ${btnColor};
      color: #ffffff;
      font-size: 16px;
      font-weight: 600;
      padding: 12px 32px;
      border-radius: 4px;
      text-decoration: none;
      display: inline-block;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      outline: none;
    `;
    button.addEventListener('input', () => {
      this.data.buttonText = button.textContent || '';
    });
    button.addEventListener('click', (e) => e.preventDefault());

    this.wrapper.appendChild(headline);
    this.wrapper.appendChild(subtitle);
    this.wrapper.appendChild(button);
  }

  save(): HeroBlockData {
    return this.data;
  }

  renderSettings(): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.style.padding = '8px';

    // Button URL input
    const urlLabel = document.createElement('label');
    urlLabel.textContent = 'Button URL:';
    urlLabel.style.cssText = 'display: block; margin-bottom: 4px; font-size: 12px; color: #666;';

    const urlInput = document.createElement('input');
    urlInput.type = 'url';
    urlInput.value = this.data.buttonUrl;
    urlInput.placeholder = 'https://...';
    urlInput.style.cssText = 'width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; margin-bottom: 12px;';
    urlInput.addEventListener('change', () => {
      this.data.buttonUrl = urlInput.value;
    });

    // Background color selector
    const colorLabel = document.createElement('label');
    colorLabel.textContent = 'Background:';
    colorLabel.style.cssText = 'display: block; margin-bottom: 4px; font-size: 12px; color: #666;';

    const colorSelect = document.createElement('select');
    colorSelect.style.cssText = 'width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px;';

    const colorOptions = [
      { value: 'wine', label: 'Wine Red' },
      { value: 'gold', label: 'Gold' },
      { value: 'teal', label: 'Sage Teal' },
      { value: 'dark', label: 'Dark' },
    ];

    colorOptions.forEach(({ value, label }) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      option.selected = this.data.backgroundColor === value;
      colorSelect.appendChild(option);
    });

    colorSelect.addEventListener('change', () => {
      this.data.backgroundColor = colorSelect.value as HeroBlockData['backgroundColor'];
      this.updateWrapper();
    });

    wrapper.appendChild(urlLabel);
    wrapper.appendChild(urlInput);
    wrapper.appendChild(colorLabel);
    wrapper.appendChild(colorSelect);

    return wrapper;
  }
}
